import { MediaPreview } from "@nexus/ui"
import type { MyBrandKpiCatalogEntry } from "./my-brand-kpi-catalog"
import { daysBetweenYmd, extractNumber, extractString, formatYmd } from "./drilldown-row"
import { formatTableValue } from "./format-table-value"
import type { ListColumnDef } from "./drilldown-list-columns"

export const MY_BRAND_LEADING_COLUMNS: ListColumnDef[] = [
  {
    key: "creative",
    header: null,
    defaultWidth: 220,
    cellClassName: "overflow-hidden",
    renderCell: (row) => {
      const objectType = extractString(row.object_type)
      const thumbnailUrl = extractString(row.thumbnail_url)
      const assetUrl = extractString(row.creative_url)
      // My Brand rows don't return included_hashes/_types (no carousel support here) —
      // any non-"image" object_type plays as a single video, matching the backend's
      // own file-extension logic (IF(object_type='image','png','mp4')).
      const isVideo = objectType.toLowerCase() !== "image"
      return (
        <div className="flex items-center gap-2.5">
          <div className="group relative shrink-0 size-16">
            <MediaPreview
              src={assetUrl}
              thumbnailSrc={thumbnailUrl}
              type={isVideo ? "video" : "image"}
              alt={objectType}
              title={objectType}
              className="size-full rounded-lg overflow-hidden cursor-pointer bg-muted"
            />
            {isVideo ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
                <span className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="size-3.5" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </div>
            ) : null}
          </div>
          <p className="text-[11px] capitalize text-muted-foreground">{objectType.toLowerCase()}</p>
        </div>
      )
    },
  },
]

const KPI_COLUMN_WIDTH = 110

/** Build the KPI columns for exactly the selected ids, in that order (drives both
 *  which columns render and their left-to-right position — see MyBrandKpiPillRow). */
export function buildMyBrandKpiColumns(
  selectedIds: string[],
  catalogById: Record<string, MyBrandKpiCatalogEntry> = {},
  currencySymbol = "$",
): ListColumnDef[] {
  return selectedIds.map((id) => {
    const entry = catalogById[id]
    return {
      key: id,
      header: entry?.label ?? id,
      defaultWidth: KPI_COLUMN_WIDTH,
      align: "right",
      cellClassName: "tabular-nums text-right text-xs text-foreground",
      renderCell: (row) =>
        formatTableValue(
          extractNumber(row[id]),
          entry?.format ?? "number",
          currencySymbol,
          entry?.decimals ?? 2,
        ),
    }
  })
}

/** Full trailing-column set for the My Brand list view: date columns + the user's
 *  selected/ordered KPI columns. */
export function buildMyBrandTrailingColumns(
  selectedIds: string[],
  catalogById: Record<string, MyBrandKpiCatalogEntry> = {},
  currencySymbol = "$",
): ListColumnDef[] {
  return [...MY_BRAND_DATE_COLUMNS, ...buildMyBrandKpiColumns(selectedIds, catalogById, currencySymbol)]
}

const MY_BRAND_DATE_COLUMNS: ListColumnDef[] = [
  {
    key: "min_date",
    header: "Min Date",
    defaultWidth: 100,
    cellClassName: "tabular-nums text-xs text-foreground",
    renderCell: (row) => formatYmd(extractString(row.min_date)),
  },
  {
    key: "max_date",
    header: "Max Date",
    defaultWidth: 100,
    cellClassName: "tabular-nums text-xs text-foreground",
    renderCell: (row) => formatYmd(extractString(row.max_date)),
  },
  {
    key: "days",
    header: "Days",
    defaultWidth: 80,
    align: "right",
    cellClassName: "tabular-nums text-right text-xs text-foreground",
    renderCell: (row) => daysBetweenYmd(extractString(row.min_date), extractString(row.max_date)) ?? "—",
  },
]
